"use client";

import {
  ArrowRight,
  ArrowUpRight,
  BrainCircuit,
  Database,
  ShieldCheck,
  Workflow,
} from "lucide-react";

import { DEMO_URL, getAllIndustries } from "./data";
import { IndustrySignal, IndustrySignalStyles } from "./IndustrySignal";
import {
  IndustryProductPreview,
  IndustryProductPreviewStyles,
} from "./IndustryProductPreview";
import {
  Reveal,
  WayakFooter,
  WayakHeader,
  WayakMarketingStyles,
} from "./LandingExperience";
import { ProductPreviewStyles } from "./ProductPreview";

const INDUSTRIES = getAllIndustries();

function UseCasesStyles() {
  return (
    <style>{`
      .ux-shell { width: min(100% - 80px,1360px); margin: 0 auto; }
      .ux-kicker { display: flex; align-items: center; gap: 11px; font-size: 10px; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; }
      .ux-kicker::before { content: ""; width: 26px; height: 1px; background: currentColor; opacity: .5; }
      .ux-title { max-width: 920px; margin: 18px 0 0; font-size: clamp(48px,5.4vw,80px); line-height: .96; letter-spacing: -.05em; }
      .ux-intro { max-width: 690px; margin: 24px 0 0; color: var(--muted); font-size: 18px; line-height: 1.6; }

      .ux-hero-wrap { padding: 12px; }
      .ux-hero { position: relative; min-height: calc(100vh - 92px); overflow: hidden; border-radius: var(--radius-panel); background: var(--ink); color: #fff; }
      .ux-hero::before { content: ""; position: absolute; inset: 0; background-image: linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px); background-size: 48px 48px; mask-image: linear-gradient(to bottom,#000,transparent 90%); }
      .ux-hero::after { content: ""; position: absolute; z-index: 0; width: min(78vw,980px); aspect-ratio: 1; right: -220px; top: -420px; border-radius: 50%; background: radial-gradient(circle,rgba(255,255,255,.12) 0,rgba(255,255,255,.052) 30%,transparent 68%); pointer-events: none; animation: v4AmbientBreath 16s cubic-bezier(.45,0,.55,1) infinite alternate; will-change: transform,opacity; }
      .ux-hero-grid { position: relative; z-index: 2; min-height: calc(100vh - 92px); display: grid; grid-template-columns: .82fr 1.18fr; gap: 72px; align-items: center; padding: 84px 0; }
      .ux-hero-copy .ux-kicker { color: rgba(255,255,255,.68); }
      .ux-hero h1 { max-width: 720px; margin: 24px 0 0; font-size: clamp(58px,6.4vw,98px); line-height: .9; letter-spacing: -.06em; }
      .ux-hero p { max-width: 620px; margin: 28px 0 0; color: rgba(255,255,255,.66); font-size: 18px; line-height: 1.6; }
      .ux-hero-actions { display: flex; gap: 12px; margin-top: 32px; }
      .ux-button { min-height: 48px; display: inline-flex; align-items: center; justify-content: center; gap: 9px; padding: 0 21px; border: 1px solid #fff; border-radius: 10px; background: #fff; color: var(--ink); font-size: 10px; font-weight: 700; letter-spacing: .13em; text-decoration: none; text-transform: uppercase; transition: transform .28s cubic-bezier(.16,1,.3,1),background .22s ease,border-color .22s ease,box-shadow .28s ease; }
      .ux-button.secondary { border-color: rgba(255,255,255,.25); background: rgba(255,255,255,.04); color: #fff; }
      .ux-button:is(:hover,:focus-visible) { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(0,0,0,.18); }
      .ux-button.secondary:is(:hover,:focus-visible) { background: rgba(255,255,255,.1); border-color: rgba(255,255,255,.42); }
      .ux-preview-label { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 12px; color: rgba(255,255,255,.52); font-size: 8px; letter-spacing: .12em; text-transform: uppercase; }
      .ux-hero .pv-stage-frame { box-shadow: 0 34px 90px rgba(0,0,0,.38); }

      .ux-index { padding: 138px 0; background: var(--paper); }
      .ux-index-head { display: flex; align-items: end; justify-content: space-between; gap: 48px; }
      .ux-index-note { max-width: 330px; color: var(--muted); font-size: 13px; line-height: 1.55; }
      .ux-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; margin-top: 68px; }
      .ux-card { position: relative; min-height: 540px; display: flex; flex-direction: column; padding: 28px; overflow: hidden; border: 1px solid var(--line); border-radius: var(--radius-section); background: #fff; color: var(--ink); text-decoration: none; transition: transform .34s cubic-bezier(.16,1,.3,1),background .26s ease,border-color .26s ease,box-shadow .34s ease; }
      .ux-card:is(:hover,:focus-visible) { transform: translateY(-3px); border-color: rgba(9,9,11,.3); background: var(--soft); box-shadow: 0 18px 44px rgba(9,9,11,.07); }
      .ux-card-top { display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--muted); font-size: 9px; letter-spacing: .12em; text-transform: uppercase; }
      .ux-card .iv-scene { margin: 22px 0 28px; }
      .ux-card h2 { max-width: 560px; margin: 0; font-size: clamp(34px,3.2vw,50px); line-height: 1; letter-spacing: -.045em; }
      .ux-card > p { max-width: 560px; margin: 16px 0 0; color: var(--muted); font-size: 14px; line-height: 1.55; }
      .ux-card-bottom { display: flex; align-items: end; justify-content: space-between; gap: 20px; margin-top: auto; padding-top: 34px; }
      .ux-card-sources { display: flex; flex-wrap: wrap; gap: 5px; }
      .ux-card-sources span { padding: 6px 7px; border: 1px solid var(--line); border-radius: 6px; color: #52525b; font-size: 7px; letter-spacing: .07em; text-transform: uppercase; }
      .ux-card-link { display: flex; align-items: center; gap: 7px; flex: none; font-size: 9px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }

      .ux-system { padding: 138px 0; background: var(--warm); }
      .ux-system-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin-top: 64px; }
      .ux-system-step { min-height: 280px; padding: 24px; border: 1px solid var(--line); border-radius: var(--radius-section); background: #fff; }
      .ux-system-step span { width: 40px; height: 40px; display: grid; place-items: center; border-radius: 10px; background: var(--ink); color: #fff; }
      .ux-system-step h3 { margin: 66px 0 0; font-size: 14px; letter-spacing: .1em; text-transform: uppercase; }
      .ux-system-step p { margin: 11px 0 0; color: var(--muted); font-size: 13px; line-height: 1.55; }

      .ux-close { padding: 16px 16px 0; }
      .ux-close-panel { position: relative; min-height: 580px; display: grid; place-items: center; padding: 80px 36px; overflow: hidden; border-radius: var(--radius-panel) var(--radius-panel) 0 0; background: var(--ink); color: #fff; text-align: center; }
      .ux-close-content { position: relative; z-index: 2; max-width: 950px; }
      .ux-close h2 { margin: 0; font-size: clamp(56px,7vw,100px); line-height: .92; letter-spacing: -.055em; }
      .ux-close p { max-width: 610px; margin: 27px auto 0; color: rgba(255,255,255,.62); font-size: 18px; line-height: 1.55; }
      .ux-close .ux-button { margin-top: 32px; }

      @media (max-width: 1080px) {
        .ux-hero-grid { grid-template-columns: 1fr; }
        .ux-hero-copy { max-width: 850px; }
        .ux-hero-preview { max-width: 900px; }
        .ux-system-grid { grid-template-columns: 1fr 1fr; }
      }
      @media (max-width: 820px) {
        .ux-shell { width: min(100% - 40px,1360px); }
        .ux-hero-wrap { padding: 8px; }
        .ux-hero { border-radius: 18px; }
        .ux-hero-grid { min-height: auto; gap: 52px; padding: 64px 0 36px; }
        .ux-hero h1 { font-size: clamp(50px,13vw,74px); }
        .ux-index, .ux-system { padding: 96px 0; }
        .ux-index-head { align-items: flex-start; flex-direction: column; }
        .ux-grid { grid-template-columns: 1fr; }
        .ux-card { min-height: 520px; }
        .ux-close { padding: 8px 8px 0; }
        .ux-close-panel { min-height: 520px; padding: 64px 20px; border-radius: 18px 18px 0 0; }
      }
      @media (max-width: 520px) {
        .ux-shell { width: min(100% - 30px,1360px); }
        .ux-hero-actions { flex-direction: column; }
        .ux-button { width: 100%; }
        .ux-title { font-size: 43px; }
        .ux-system-grid { grid-template-columns: 1fr; }
        .ux-card-bottom { align-items: flex-start; flex-direction: column; }
      }
      @media (prefers-reduced-motion: reduce) { .ux-hero::after,.ux-card:is(:hover,:focus-visible),.ux-button { animation: none !important; transform: none !important; } }
    `}</style>
  );
}

export default function UseCasesPage() {
  const featured = INDUSTRIES[0];
  const system = [
    [
      Database,
      "Connect",
      "Bring live systems, documents, and records into one governed working context.",
    ],
    [
      BrainCircuit,
      "Understand",
      "Model the entities, relationships, rules, and history behind the decision.",
    ],
    [
      Workflow,
      "Operate",
      "Turn repeated judgment and follow-up into a visible, reusable playbook.",
    ],
    [
      ShieldCheck,
      "Control",
      "Route consequential decisions to people and retain the evidence behind every outcome.",
    ],
  ] as const;
  return (
    <div className="v4-landing">
      <WayakMarketingStyles />
      <ProductPreviewStyles />
      <IndustryProductPreviewStyles />
      <IndustrySignalStyles />
      <UseCasesStyles />
      <WayakHeader />
      <main>
        <section id="top" className="ux-hero-wrap">
          <div className="ux-hero">
            <div className="ux-shell ux-hero-grid">
              <Reveal className="ux-hero-copy" immediate>
                <div className="ux-kicker">Wayak use cases</div>
                <h1>Start narrow. Build an operating system.</h1>
                <p>
                  Choose the workflow where delay, inconsistency, or manual
                  follow-up costs the most. Wayak connects its context, governs
                  its decisions, and gives the next workflow a head start.
                </p>
                <div className="ux-hero-actions">
                  <a
                    className="ux-button"
                    href={DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book a demo <ArrowUpRight size={14} />
                  </a>
                  <a className="ux-button secondary" href="#industries">
                    Explore industries <ArrowRight size={14} />
                  </a>
                </div>
              </Reveal>
              <Reveal className="ux-hero-preview" delay={100} immediate>
                <div className="ux-preview-label">
                  <span>Illustrative Wayak workflow</span>
                  <span>Manufacturing · cost variance</span>
                </div>
                <IndustryProductPreview data={featured} exampleIndex={0} />
              </Reveal>
            </div>
          </div>
        </section>

        <section id="industries" className="ux-index">
          <div className="ux-shell">
            <Reveal className="ux-index-head">
              <div>
                <div className="ux-kicker">Choose the operational wedge</div>
                <h2 className="ux-title">
                  The same Wayak layer, shaped around the work.
                </h2>
              </div>
              <p className="ux-index-note">
                Every page shows the actual sources, record types, agent
                finding, human control point, and value created for that
                workflow.
              </p>
            </Reveal>
            <div className="ux-grid">
              {INDUSTRIES.map((industry, index) => (
                <Reveal key={industry.slug} delay={(index % 2) * 70}>
                  <a className="ux-card" href={`/use-cases/${industry.slug}`}>
                    <div className="ux-card-top">
                      <span>
                        {String(index + 1).padStart(2, "0")} · {industry.name}
                      </span>
                      <ArrowUpRight size={15} />
                    </div>
                    <IndustrySignal slug={industry.slug} density="index" />
                    <h2>{industry.headline}</h2>
                    <p>{industry.sub}</p>
                    <div className="ux-card-bottom">
                      <div className="ux-card-sources">
                        {industry.sources.slice(0, 3).map((source) => (
                          <span key={source}>{source}</span>
                        ))}
                      </div>
                      <span className="ux-card-link">
                        See the system <ArrowRight size={13} />
                      </span>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="ux-system">
          <div className="ux-shell">
            <Reveal>
              <div className="ux-kicker">One reusable architecture</div>
              <h2 className="ux-title">
                The industry changes. The operating loop compounds.
              </h2>
              <p className="ux-intro">
                Each new workflow reuses the connected context, organization
                model, controls, and run history already built into the
                platform.
              </p>
            </Reveal>
            <div className="ux-system-grid">
              {system.map(([Icon, title, body], index) => (
                <Reveal key={title} delay={index * 60}>
                  <article className="ux-system-step">
                    <span>
                      <Icon size={18} />
                    </span>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="ux-close">
          <div className="ux-close-panel">
            <Reveal className="ux-close-content">
              <h2>Bring us the workflow everyone hates.</h2>
              <p>
                We’ll show you where Wayak creates the first measurable win—and
                how that win becomes infrastructure for the next one.
              </p>
              <a
                className="ux-button"
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a demo <ArrowUpRight size={14} />
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <WayakFooter />
    </div>
  );
}
